import { RoutePaths } from "enums/RoutePaths";
import { signOut, User } from "firebase/auth";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Alert,
  Button,
  Icon,
  styles as ui,
  Textarea,
  TextField,
} from "stories/Admin";
import { auth } from "../../firebase";
import useFetch from "../../hooks/useFetch";
import loginStyles from "../Login/Login.module.scss";
import styles from "./NoAccess.module.scss";

const FOR_MOSQUES_URL = "https://xn--ibnn-hra.no/for-moskeer";

interface Mosque {
  orgNr: string;
  orgName: string;
  name?: string;
}

interface MyAdminRequest {
  id: number;
  mosqueOrgNr: string;
  mosqueName?: string;
  status: string;
}

/**
 * Innlogget (f.eks. med Google), men ikke admin for noen moské: forklar det og la brukeren
 * søke om moské-admin (`POST /api/v1/admin-requests`, samme som «Søk om moské-admin» i appene).
 */
const NoAccess: React.FC = () => {
  const navigate = useNavigate();
  const mosquesApi = useFetch<Mosque[]>();
  const mineApi = useFetch<MyAdminRequest[]>();
  const sendApi = useFetch<{ id: number; status: string }>();
  const partnersApi = useFetch<Array<{ id: number }>>();

  const [user, setUser] = useState<User | null>(null);
  const [mosques, setMosques] = useState<Mosque[] | null>(null);
  const [pending, setPending] = useState<MyAdminRequest[]>([]);
  const [orgNr, setOrgNr] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** API-et kan ikke nås herfra (f.eks. App Check): henvis til appen. */
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      await auth.authStateReady();
      const current = auth.currentUser;
      if (!current || current.isAnonymous) {
        navigate(RoutePaths.Login, { replace: true });
        return;
      }
      if (!active) return;
      // Admin for en partner (f.eks. en forening med iBønn-widgeten), men ikke for en moské: til partnersiden.
      const partners = await partnersApi.execute("/api/v1/me/partners", "GET");
      if (!active) return;
      if (Array.isArray(partners) && partners.length > 0) {
        navigate(RoutePaths.Partner, { replace: true });
        return;
      }
      setUser(current);
      setFullName(current.displayName || "");
      setEmail(current.email || "");

      const [list, mine] = await Promise.all([
        mosquesApi.execute("/api/v1/mosques", "GET"),
        mineApi.execute("/api/v1/me/admin-requests", "GET"),
      ]);
      if (!active) return;
      if (list) {
        setMosques(
          [...list].sort((a, b) => a.orgName.localeCompare(b.orgName, "nb")),
        );
      } else {
        setMosques([]);
        setUnavailable(true);
      }
      if (mine) setPending(mine.filter((r) => r.status === "PENDING"));
    })();
    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(
    () =>
      (mosques || []).map((m) => ({
        value: m.orgNr,
        label:
          m.name && m.name !== m.orgName
            ? `${m.orgName} (${m.name})`
            : m.orgName,
      })),
    [mosques],
  );

  const logout = async () => {
    await signOut(auth).catch(() => undefined);
    sessionStorage.clear();
    navigate(RoutePaths.Login, { replace: true });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!orgNr) {
      setError("Velg moskeen du vil bli admin for.");
      return;
    }
    setSending(true);
    const result = await sendApi.execute("/api/v1/admin-requests", "POST", {
      mosqueOrgNr: orgNr,
      fullName: fullName.trim(),
      role: role.trim(),
      phone: phone.trim(),
      email: email.trim(),
      message: message.trim() || undefined,
    });
    setSending(false);
    if (result) {
      setSent(true);
      return;
    }
    const err = sendApi.lastError();
    if (err && [400, 409, 429].includes(err.code) && err.message) {
      setError(err.message);
    } else if (err && err.code !== 520 && err.code >= 400 && err.code < 500) {
      setUnavailable(true);
    } else {
      setError("Kunne ikke sende søknaden. Sjekk nettet og prøv igjen.");
    }
  };

  return (
    <div className={`${ui.root} ${loginStyles.login}`}>
      <section className={loginStyles.hero}>
        <span className={loginStyles.brand}>
          <span className={loginStyles.brandMark}>
            <Icon name="moon" size={24} strokeWidth={2.2} />
          </span>
          iBønn Admin
        </span>
        <div className={loginStyles.heroText}>
          <span className={loginStyles.heroTitle}>
            Søk om å bli admin for moskeen din.
          </span>
          <span className={loginStyles.heroLead}>
            Som admin legger du inn jamat-tider, push-meldinger, plakater til
            TV-skjermen og innhold i iBønn-appen.
          </span>
        </div>
      </section>

      <section className={loginStyles.formSide}>
        <div className={styles.panel}>
          <div>
            <h1 className={ui.h1}>Du har ikke admin-tilgang ennå</h1>
            <div className={ui.sub}>
              {user?.email ? (
                <>
                  Du er logget inn som <strong>{user.email}</strong>, men
                  brukeren er ikke admin for noen moské i iBønn.
                </>
              ) : (
                "Brukeren din er ikke admin for noen moské i iBønn."
              )}
            </div>
          </div>

          {pending.length > 0 && !sent && (
            <Alert tone="info">
              Du har en søknad som venter på svar
              {pending[0].mosqueName ? ` (${pending[0].mosqueName})` : ""}. Du
              får beskjed når den er behandlet – logg inn igjen her etterpå.
            </Alert>
          )}

          {sent ? (
            <Alert tone="ok">
              Søknaden er sendt! iBønn går gjennom den og gir deg beskjed. Når
              den er godkjent, logger du inn her igjen med samme bruker.
            </Alert>
          ) : unavailable ? (
            <Alert tone="warn">
              Søknaden kan ikke sendes herfra akkurat nå. Søk i iBønn-appen
              (Profil → «Er du admin?» → Søk om moské-admin) med samme bruker,
              eller les mer på{" "}
              <a href={FOR_MOSQUES_URL} target="_blank" rel="noreferrer">
                ibønn.no/for-moskeer
              </a>
              .
            </Alert>
          ) : (
            <form className={styles.form} onSubmit={submit} noValidate>
              <h2 className={styles.title}>Søk om moské-admin</h2>
              {error && <Alert tone="error">{error}</Alert>}

              <div className={styles.field}>
                <label className={styles.label} htmlFor="no-access-mosque">
                  Moské <span aria-hidden="true">*</span>
                </label>
                <Select
                  inputId="no-access-mosque"
                  options={options}
                  isLoading={mosques === null}
                  placeholder="Søk etter moskeen …"
                  noOptionsMessage={() => "Fant ingen moské"}
                  loadingMessage={() => "Henter moskeer …"}
                  value={options.find((o) => o.value === orgNr) || null}
                  onChange={(o) => setOrgNr(o ? o.value : null)}
                  isClearable
                />
              </div>
              <TextField
                label="Fullt navn"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                maxLength={100}
              />
              <TextField
                label="Rolle i moskeen"
                required
                placeholder="F.eks. styreleder, imam eller frivillig"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                maxLength={100}
              />
              <TextField
                label="Telefon"
                required
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                maxLength={20}
              />
              <TextField
                label="E-post vi kan kontakte deg på"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <Textarea
                label="Melding (valgfri)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                maxChars={2000}
              />
              <Button type="submit" size="lg" block loading={sending}>
                {sending ? "Sender …" : "Send søknad"}
              </Button>
            </form>
          )}

          <span className={loginStyles.request}>
            Ny moské i iBønn?{" "}
            <Link to={RoutePaths.MosqueRequestForm}>Send forespørsel</Link>
            {" · "}
            <a href={FOR_MOSQUES_URL} target="_blank" rel="noreferrer">
              Les mer for moskeer
            </a>
          </span>
          <Button variant="ghost" onClick={logout} block>
            Logg ut og bytt bruker
          </Button>
        </div>
      </section>
    </div>
  );
};

export default NoAccess;
